import type { ReactNode } from "react";

import { cn } from "../lib/cn";

/**
 * The persistent settings-field `Saved` state.
 *
 * From `05-system-states.md` §8: a settings field showing `Saved` beside it,
 * which stays until the value changes again or the fan leaves the screen. The
 * component never clears itself on a timer. Clearing is the owning field's
 * job: it flips `saved` back to false when its value changes.
 *
 * This exists so a settings field never announces itself with a toast.
 */

export type typeSavedNoteProps = {
  /**
   * True after a successful save. Stays true until the owning field's value
   * changes again or the fan leaves the screen — the component never clears
   * itself on a timer.
   */
  saved: boolean;
  /** Overrides the default word. Sentence case, American English. */
  label?: string | undefined;
  className?: string | undefined;
};

export const SavedNote = ({
  saved,
  label,
  className,
}: typeSavedNoteProps): ReactNode => {
  if (!saved) return null;

  return (
    <span
      role="status"
      aria-live="polite"
      data-testid="saved-note"
      className={cn(
        "inline-flex items-center gap-1 text-caption text-success",
        className,
      )}
    >
      <svg
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 20 20"
        fill="none"
        className="h-icon-sm w-icon-sm shrink-0"
      >
        <path
          d="m6.25 10.25 2.5 2.5 5-5.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {label ?? "Saved"}
    </span>
  );
};
